import axios from "axios";
import store from "@/store";
import router from "../router";
import ElementUI from "element-ui";
import i18n from "@/i18n";

/* 取消接口请求的操作器:请根据版本选择合适的操作器 */
class CancelControl {
  /* 构造函数 */
  constructor() {
    this.allCancelApi = {};
  }
  /* 此方法记录请求函数 calcelAPI:接口名称或者url */

  setAbortAPI(calcelAPI) {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    this.allCancelApi[calcelAPI] = source;
    return source.token;
  }
  /* 执行此方法取消请求 calcelAPI:接口名称或者url */

  abort(calcelAPI) {
    if (this.allCancelApi[calcelAPI]) {
      this.allCancelApi[calcelAPI].cancel("Cancel");
    }
  }
}
export const cancelControl = new CancelControl();
// 创建axios实例
let instance = axios.create();

const loginApiList = [
  "ytb/user/cartoonLogin",
  "ytb/phone/login",
  "ai/phone/login",
  "ytb/phone/code/send",
  "ytb/phone/register",
  "ytb/invite-link/lock",
  "ytb/user/forgot/password",
  "ytb/team/invite/detail",
  "ytb/team/invite/code/send",
  "ytb/team/invite/register",
];
let hasAuthenticatedResponse = false;
let authMessageVisible = false;
let authExpiredHandled = false;

// 部分业务接口会用 401 表示资源不存在，不能走全局登录失效
function shouldSkipAuthExpired(config = {}) {
  return Boolean(config && config.skipAuthExpired);
}

function isLoginRequest(config = {}) {
  const url = config.url || "";
  return loginApiList.some((api) => url.indexOf(api) >= 0);
}

function getResponseToken(headers) {
  if (!headers) {
    return "";
  }
  if (typeof headers.get === "function") {
    return (
      headers.get("token") ||
      headers.get("Token") ||
      headers.get("Authorization") ||
      headers.get("authorization") ||
      ""
    );
  }
  return (
    headers.token ||
    headers.Token ||
    headers.TOKEN ||
    headers.Authorization ||
    headers.authorization ||
    ""
  );
}

function normalizeToken(token) {
  if (token === undefined || token === null) {
    return "";
  }
  const value = String(token).trim().replace(/^Bearer\s+/i, "").trim();
  if (
    !value ||
    value === "undefined" ||
    value === "null" ||
    value === "0" ||
    value.toLowerCase() === "false"
  ) {
    return "";
  }
  return value;
}

function getResponseBodyToken(body = {}) {
  const data = body && body.data;
  const user = data && typeof data === "object" ? data.user || {} : {};
  const tokenInfo =
    data && typeof data === "object" ? data.tokenInfo || {} : {};
  return (
    normalizeToken(body.token) ||
    normalizeToken(body.accessToken) ||
    normalizeToken(body.access_token) ||
    normalizeToken(body.tokenValue) ||
    normalizeToken(body.jwt) ||
    normalizeToken(body.authToken) ||
    normalizeToken(body.sessionToken) ||
    normalizeToken(body.loginToken) ||
    normalizeToken(body.authorization) ||
    normalizeToken(body.Authorization) ||
    normalizeToken(data && data.token) ||
    normalizeToken(data && data.accessToken) ||
    normalizeToken(data && data.access_token) ||
    normalizeToken(data && data.tokenValue) ||
    normalizeToken(data && data.jwt) ||
    normalizeToken(data && data.authToken) ||
    normalizeToken(data && data.sessionToken) ||
    normalizeToken(data && data.loginToken) ||
    normalizeToken(data && data.authorization) ||
    normalizeToken(data && data.Authorization) ||
    normalizeToken(user.token) ||
    normalizeToken(user.accessToken) ||
    normalizeToken(user.access_token) ||
    normalizeToken(tokenInfo.token) ||
    normalizeToken(tokenInfo.accessToken) ||
    normalizeToken(tokenInfo.access_token) ||
    normalizeToken(tokenInfo.tokenValue) ||
    ""
  );
}

function markAuthenticatedResponse() {
  hasAuthenticatedResponse = true;
  authExpiredHandled = false;
}

function resetAuthExpiredHandled() {
  authExpiredHandled = false;
}

function isSuccessfulResponseCode(code) {
  return code == null || code === 200 || code === "200";
}

function getRequestToken(config = {}) {
  return getResponseToken(config.headers);
}

function setRequestHeader(headers, key, value) {
  if (typeof headers.set === "function") {
    headers.set(key, value);
    return;
  }
  headers[key] = value;
}

function hasRequestHeader(headers, key) {
  if (!headers) {
    return false;
  }
  if (typeof headers.has === "function") {
    return headers.has(key);
  }
  const target = String(key).toLowerCase();
  return Object.keys(headers).some((name) => name.toLowerCase() === target);
}

function getAcceptLanguage() {
  if (i18n.locale === "en-US") {
    return "en-US";
  }
  if (i18n.locale === "yue") {
    return "yue";
  }
  return "zh-CN";
}

function applyAuthHeaders(headers, token) {
  setRequestHeader(headers, "token", token);
  if (!hasRequestHeader(headers, "Authorization")) {
    const authorization = /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
    setRequestHeader(headers, "Authorization", authorization);
  }
  setRequestHeader(headers, "ngrok-skip-browser-warning", 69420);
}

function isStaleAuthExpiredResponse(config = {}) {
  const requestToken = normalizeToken(getRequestToken(config));
  const currentToken = normalizeToken(window.localStorage.getItem("token"));
  if (!requestToken && currentToken) {
    return true;
  }
  return Boolean(requestToken && requestToken !== currentToken);
}

function syncResponseToken(response) {
  if (response && response.config && response.config.skipTokenSync) return;
  if (!response || response.status < 200 || response.status >= 300) {
    return;
  }
  const body = (response && response.data) || {};
  const data = body.data || {};
  const refreshedToken =
    data && data.refreshed === true ? normalizeToken(data.token) : "";
  const responseCode = body.code;
  const isSuccessfulResponse = isSuccessfulResponseCode(responseCode);
  if (!refreshedToken && !isSuccessfulResponse) {
    return;
  }
  const responseToken =
    refreshedToken ||
    normalizeToken(getResponseToken(response && response.headers)) ||
    getResponseBodyToken(body);
  if (!responseToken) {
    return;
  }
  const currentToken = window.localStorage.getItem("token");
  if (responseToken !== currentToken) {
    window.localStorage.setItem("token", responseToken);
    notifyAuthTokenChanged();
  }
  markAuthenticatedResponse();
}

function notifyAuthTokenChanged() {
  window.dispatchEvent(new CustomEvent("auth-token-changed"));
}

function goLogin(message, options = {}) {
  if (authExpiredHandled) {
    return;
  }
  authExpiredHandled = true;
  const shouldSilentMessage =
    options.silentBeforeAuthenticatedResponse && !hasAuthenticatedResponse;
  if (
    window.localStorage.getItem("token") != null &&
    message &&
    !shouldSilentMessage &&
    !authMessageVisible
  ) {
    authMessageVisible = true;
    ElementUI.Message.warning({
      message,
      onClose: () => {
        authMessageVisible = false;
      },
    });
  }
  window.localStorage.removeItem("token");
  notifyAuthTokenChanged();
  if (router.currentRoute && router.currentRoute.path === "/login") {
    return;
  }
  router.push("/login");
}

function handleAuthExpiredResponse(message, options = {}, config = {}) {
  if (isStaleAuthExpiredResponse(config)) {
    return new Promise(() => {});
  }
  goLogin(message, options);
  return new Promise(() => {});
}

instance.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  setRequestHeader(config.headers, "Accept-Language", getAcceptLanguage());
  // 如果不是调用登录接口的话
  if (!isLoginRequest(config)) {
    // token
    let token =
      normalizeToken(getResponseToken(config.headers)) ||
      normalizeToken(window.localStorage.getItem("token"));
    // let token = "";
    if (token == null || token == "") {
      router.push("/login");
    } else {
      // 添加header
      applyAuthHeaders(config.headers, token);
    }
  }
  if (config.multipleCancel) {
    // 如果设置了连续请求时取消之前的请求
    cancelControl.abort(config.url);
    config.cancelToken = cancelControl.setAbortAPI(
      config.url,
    ); /* axios  v0.22.0以下版本  */
    // config.signal = cancelControl.setAbortAPI(url)  /* axios  v0.22.0以上版本 */
  }
  // 返回
  return config;
});
instance.interceptors.response.use(
  (response) => {
    if (response && response.data) {
      if (
        response.data.code == 401 ||
        response.data.code == 70006 ||
        response.data.code == 70004 ||
        response.data.code == 70008 ||
        response.data.code == 70009 ||
        response.data.code == 500405
      ) {
        if (isLoginRequest(response.config)) {
          // 登录接口的处理逻辑
          return response;
        }
        // 调用方自行处理 401，避免把「当前没有邀请」误判成登录失效
        if (shouldSkipAuthExpired(response.config)) {
          return response;
        }
        return handleAuthExpiredResponse(
          i18n.t("auth.loginExpired"),
          {
            silentBeforeAuthenticatedResponse: true,
          },
          response.config,
        );
      } else if (response.data.code == 500406) {
        return handleAuthExpiredResponse(
          i18n.t("auth.otherDeviceLogin"),
          {},
          response.config,
        );
      } else {
        if (isLoginRequest(response.config)) {
          if (isSuccessfulResponseCode(response.data.code)) {
            resetAuthExpiredHandled();
          }
        } else {
          syncResponseToken(response);
          markAuthenticatedResponse();
        }
        return response;
      }
    } else {
      syncResponseToken(response);
      if (!isLoginRequest(response.config)) {
        markAuthenticatedResponse();
      }
      return response;
    }
  },
  function (error) {
    const errorBody = (error.response && error.response.data) || null;
    // 账号本人或所属客户被后台冻结：展示后端下发的冻结原因并退出登录。
    // 必须排在通用 401 分支之前，否则会被笼统的「登录已过期」盖掉；
    // 这里也不能静默提示，用户需要知道是被冻结而不是登录过期。
    if (errorBody && (errorBody.code == 70010 || errorBody.code == 70011)) {
      if (isLoginRequest(error.response.config)) {
        return error.response;
      }
      return handleAuthExpiredResponse(
        errorBody.message || i18n.t("auth.accountFrozen"),
        {},
        error.response.config,
      );
    }
    if (
      error.response &&
      (error.response.status == 401 ||
        (error.response.data && error.response.data.code == 401))
    ) {
      if (isLoginRequest(error.response.config)) {
        return error.response;
      }
      // 调用方自行处理 HTTP 401，避免误清 token 并跳登录页
      if (shouldSkipAuthExpired(error.response.config)) {
        return Promise.reject(error);
      }
      return handleAuthExpiredResponse(
        i18n.t("auth.loginExpired"),
        {
          silentBeforeAuthenticatedResponse: true,
        },
        error.response.config,
      );
    }
    return Promise.reject(error);
  },
);
export default instance;
